import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAttendance } from '../context/AttendanceContext';
import { format } from 'date-fns';

const { FiAward, FiTrendingUp, FiCalendar, FiUsers, FiClock, FiTarget, FiDollarSign, FiRefreshCw, FiChevronDown, FiChevronUp, FiCheckCircle, FiXCircle, FiEdit2, FiSave, FiTrash2, FiX, FiCheckSquare, FiSquare } = FiIcons;

const QuarterlyResults = ({ onNavigate }) => {
  const { 
    currentQuarter, 
    getAttendanceStats, 
    getActiveMembers, 
    weeklyAttendance, 
    startNewQuarter, 
    members, 
    updateMeetingAttendance, 
    deleteMeetingAttendance 
  } = useAttendance();

  const [selectedWinner, setSelectedWinner] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showConfirmNewQuarter, setShowConfirmNewQuarter] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [editFormData, setEditFormData] = useState({ attended: false, minutesLate: 0 });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [meetingToDelete, setMeetingToDelete] = useState(null);

  const attendanceStats = getAttendanceStats();
  const activeMembers = getActiveMembers();
  const totalMeetings = weeklyAttendance.length;
  const completedMeetings = weeklyAttendance.filter(m => !m.isActive).length;

  // Updated eligibility logic - only members with perfect attendance are eligible
  const eligibleMembers = attendanceStats.filter(
    member => member.active && member.attendanceRate === 100
  );

  const selectRandomWinner = () => {
    if (eligibleMembers.length === 0) return;
    const randomIndex = Math.floor(Math.random() * eligibleMembers.length);
    setSelectedWinner(eligibleMembers[randomIndex]);
  };

  const getAttendanceColor = (rate) => {
    if (rate >= 80) return 'text-green-600';
    if (rate >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPunctualityColor = (rate) => {
    if (rate >= 90) return 'text-green-600';
    if (rate >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const handleStartNewQuarter = () => {
    setShowConfirmNewQuarter(true);
  };

  const confirmNewQuarter = () => {
    startNewQuarter();
    setShowConfirmNewQuarter(false);
  };

  const getMemberMeetingHistory = (memberId) => {
    return weeklyAttendance
      .filter(meeting => !meeting.isActive) // Only completed meetings
      .map(meeting => {
        const attendanceRecord = meeting.attendees.find(a => a.memberId === memberId);
        return {
          meetingId: meeting.id,
          date: meeting.date,
          attended: !!attendanceRecord,
          minutesLate: attendanceRecord ? attendanceRecord.minutesLate : null,
          checkInTime: attendanceRecord ? attendanceRecord.checkInTime : null
        };
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort by date, newest first
  };

  const handleEditMeeting = (meeting, memberId) => {
    setEditingMeeting({ meetingId: meeting.meetingId, memberId: memberId, date: meeting.date });
    setEditFormData({ attended: meeting.attended, minutesLate: meeting.minutesLate || 0 });
  };

  const handleCancelEdit = () => {
    setEditingMeeting(null);
    setEditFormData({ attended: false, minutesLate: 0 });
  };

  const handleSaveEdit = () => {
    if (!editingMeeting) return;
    
    updateMeetingAttendance(
      editingMeeting.meetingId,
      editingMeeting.memberId,
      editFormData.attended,
      editFormData.minutesLate
    );
    setEditingMeeting(null);
  };

  const confirmDeleteAttendance = (meeting, memberId) => {
    setMeetingToDelete({ meetingId: meeting.meetingId, memberId: memberId, date: meeting.date });
    setShowDeleteConfirm(true);
  };

  const handleDeleteAttendance = () => {
    if (!meetingToDelete) return;
    
    deleteMeetingAttendance(
      meetingToDelete.meetingId,
      meetingToDelete.memberId
    );
    setShowDeleteConfirm(false);
    setMeetingToDelete(null);
  };

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <h2 className="text-3xl font-bold text-gray-800">Quarterly Results</h2>
        <p className="text-gray-600 mt-2">
          {currentQuarter} - Prize Selection & Statistics
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="card bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200"
      >
        <div className="card-body p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-purple-800 flex items-center">
                <SafeIcon icon={FiRefreshCw} className="mr-2" />
                Quarter Management
              </h3>
              <p className="text-purple-600 mt-1">
                Start a new quarter to reset attendance tracking
              </p>
            </div>
            <button
              onClick={handleStartNewQuarter}
              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center justify-center"
            >
              <SafeIcon icon={FiRefreshCw} className="mr-2" />
              Start New Quarter
            </button>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { title: 'Total Meetings', value: totalMeetings, icon: FiCalendar, color: 'bg-blue-500' },
          { title: 'Active Members', value: activeMembers.length, icon: FiUsers, color: 'bg-green-500' },
          { title: 'Eligible for Prize', value: eligibleMembers.length, icon: FiTarget, color: 'bg-purple-500' },
          { title: 'Prize Amount', value: '$500', icon: FiDollarSign, color: 'bg-yellow-500' }
        ].map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="card"
          >
            <div className="card-body text-center">
              <div className={`w-16 h-16 ${stat.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                <SafeIcon icon={stat.icon} className="text-white text-2xl" />
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">{stat.title}</h3>
              <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {showConfirmNewQuarter && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-red-600 mb-4">Confirm New Quarter</h3>
              <p className="text-gray-700 mb-4">
                Are you sure you want to start a new quarter? This will:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-1">
                <li>Save all current quarter data</li>
                <li>Reset all attendance records</li>
                <li>Reset all consecutive absences</li>
                <li>Reactivate all members</li>
              </ul>
              <div className="flex space-x-3">
                <button
                  onClick={confirmNewQuarter}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded"
                >
                  Start New Quarter
                </button>
                <button
                  onClick={() => setShowConfirmNewQuarter(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {showDeleteConfirm && meetingToDelete && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-red-600 mb-4">Confirm Deletion</h3>
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete this attendance record for the meeting on {format(new Date(meetingToDelete.date), 'MMMM d, yyyy')}?
              </p>
              <p className="text-red-600 mb-6 text-sm">This action cannot be undone.</p>
              <div className="flex space-x-3">
                <button
                  onClick={handleDeleteAttendance}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded flex items-center justify-center"
                >
                  <SafeIcon icon={FiTrash2} className="mr-2" />
                  Delete Record
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="card"
        >
          <div className="card-header">
            <h3 className="text-xl font-semibold flex items-center">
              <SafeIcon icon={FiAward} className="mr-3" />
              Prize Selection
            </h3>
          </div>
          <div className="card-body">
            {eligibleMembers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <SafeIcon icon={FiUsers} className="mx-auto text-4xl mb-4" />
                <p className="text-lg">No eligible members</p>
                <p className="text-sm">Members need perfect attendance (100%) to be eligible</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="text-center">
                  <p className="text-gray-600 mb-4">
                    {eligibleMembers.length} member{eligibleMembers.length !== 1 ? 's' : ''} eligible for the $500 prize
                  </p>
                  <button
                    onClick={selectRandomWinner}
                    className="btn-primary flex items-center mx-auto"
                  >
                    <SafeIcon icon={FiAward} className="mr-2" />
                    Select Random Winner
                  </button>
                </div>

                {selectedWinner && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="bg-gradient-to-r from-yellow-50 to-yellow-100 border-2 border-yellow-300 rounded-lg p-6 text-center"
                  >
                    <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <SafeIcon icon={FiAward} className="text-white text-2xl" />
                    </div>
                    <h4 className="text-2xl font-bold text-yellow-800 mb-2">
                      🎉 WINNER! 🎉
                    </h4>
                    <p className="text-xl font-semibold text-yellow-700 mb-2">
                      {selectedWinner.name}
                    </p>
                    <p className="text-yellow-600 mb-4">
                      {selectedWinner.company}
                    </p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-yellow-600">Attendance Rate</p>
                        <p className="font-semibold text-yellow-800">
                          {selectedWinner.attendanceRate}%
                        </p>
                      </div>
                      <div>
                        <p className="text-yellow-600">Total Late Minutes</p>
                        <p className="font-semibold text-yellow-800">
                          {selectedWinner.totalMinutesLate}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="card"
        >
          <div className="card-header">
            <h3 className="text-xl font-semibold flex items-center">
              <SafeIcon icon={FiTrendingUp} className="mr-3" />
              Attendance & Punctuality Statistics
            </h3>
          </div>
          <div className="card-body">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-blue-800 mb-2">Updated Prize Rules:</h4>
              <p className="text-blue-700 text-sm">
                Only members with <strong>perfect attendance (100%)</strong> are eligible for the quarterly prize. 
                Members are removed from the group after <strong>3 consecutive absences</strong>.
              </p>
            </div>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {attendanceStats.map((member, index) => (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className={`p-4 rounded-lg border ${
                    member.active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-300 opacity-75'
                  } cursor-pointer hover:bg-gray-50`}
                  onClick={() => setSelectedMember(selectedMember?.id === member.id ? null : member)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-900">{member.name}</h4>
                      <p className="text-sm text-gray-600">{member.company || 'No company'}</p>
                    </div>
                    <div className="text-right flex items-center">
                      <p className="text-sm text-gray-600 mr-2">
                        {member.attendanceCount}/{totalMeetings} meetings
                      </p>
                      <SafeIcon 
                        icon={selectedMember?.id === member.id ? FiChevronUp : FiChevronDown} 
                        className="text-gray-400" 
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Attendance:</span>
                        <span className={`font-semibold ${getAttendanceColor(member.attendanceRate)}`}>
                          {member.attendanceRate}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div
                          className={`h-2 rounded-full ${
                            member.attendanceRate >= 80 ? 'bg-green-500' : 
                            member.attendanceRate >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${member.attendanceRate}%` }}
                        ></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Punctuality:</span>
                        <span className={`font-semibold ${getPunctualityColor(member.punctualityRate)}`}>
                          {member.punctualityRate}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div
                          className={`h-2 rounded-full ${
                            member.punctualityRate >= 90 ? 'bg-green-500' : 
                            member.punctualityRate >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${member.punctualityRate}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center text-gray-600">
                      <SafeIcon icon={FiClock} className="mr-1" />
                      <span>Total late: {member.totalMinutesLate} min</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <SafeIcon icon={FiClock} className="mr-1" />
                      <span>Avg late: {member.averageMinutesLate} min</span>
                    </div>
                  </div>

                  {selectedMember?.id === member.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-4 pt-4 border-t border-gray-200"
                    >
                      <div className="flex justify-between items-center mb-3">
                        <h5 className="font-medium text-gray-800">Meeting History</h5>
                        <div className="text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                          Click edit icon to modify records
                        </div>
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {getMemberMeetingHistory(member.id).length > 0 ? (
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="text-left p-2">Date</th>
                                <th className="text-center p-2">Status</th>
                                <th className="text-center p-2">Check-in Time</th>
                                <th className="text-right p-2">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {getMemberMeetingHistory(member.id).map((meeting, idx) => (
                                <tr key={idx} className="border-b border-gray-100">
                                  {editingMeeting && editingMeeting.meetingId === meeting.meetingId && editingMeeting.memberId === member.id ? (
                                    // Edit Mode
                                    <>
                                      <td className="p-2">
                                        {format(new Date(meeting.date), 'MMM d, yyyy')}
                                      </td>
                                      <td className="p-2">
                                        <div className="flex items-center justify-center space-x-2">
                                          <button
                                            className={`p-1 rounded-md ${editFormData.attended ? 'bg-green-100' : 'bg-gray-100'}`}
                                            onClick={() => setEditFormData({ ...editFormData, attended: !editFormData.attended })}
                                          >
                                            <SafeIcon 
                                              icon={editFormData.attended ? FiCheckSquare : FiSquare} 
                                              className={editFormData.attended ? "text-green-600" : "text-gray-400"} 
                                            />
                                          </button>
                                          <span>{editFormData.attended ? 'Present' : 'Absent'}</span>
                                        </div>
                                      </td>
                                      <td className="p-2 text-center">
                                        {editFormData.attended && (
                                          <div className="flex items-center justify-center">
                                            <span className="mr-2">Late minutes:</span>
                                            <input
                                              type="number"
                                              min="0"
                                              value={editFormData.minutesLate}
                                              onChange={(e) => setEditFormData({ ...editFormData, minutesLate: parseInt(e.target.value) || 0 })}
                                              className="w-16 text-center border border-gray-300 rounded-md p-1"
                                            />
                                          </div>
                                        )}
                                      </td>
                                      <td className="p-2 text-right">
                                        <div className="flex items-center justify-end space-x-2">
                                          <button
                                            onClick={handleSaveEdit}
                                            className="p-1.5 bg-green-100 text-green-700 rounded-md hover:bg-green-200"
                                          >
                                            <SafeIcon icon={FiSave} className="text-green-700" />
                                          </button>
                                          <button
                                            onClick={handleCancelEdit}
                                            className="p-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                                          >
                                            <SafeIcon icon={FiX} className="text-gray-700" />
                                          </button>
                                        </div>
                                      </td>
                                    </>
                                  ) : (
                                    // View Mode
                                    <>
                                      <td className="p-2">
                                        {format(new Date(meeting.date), 'MMM d, yyyy')}
                                      </td>
                                      <td className="p-2 text-center">
                                        {meeting.attended ? (
                                          <div className="flex items-center justify-center">
                                            <SafeIcon 
                                              icon={FiCheckCircle} 
                                              className={meeting.minutesLate > 0 ? "text-orange-500" : "text-green-500"} 
                                            />
                                            <span className={`ml-1 ${meeting.minutesLate > 0 ? "text-orange-500" : "text-green-500"}`}>
                                              {meeting.minutesLate > 0 ? `${meeting.minutesLate}m late` : "Present"}
                                            </span>
                                          </div>
                                        ) : (
                                          <div className="flex items-center justify-center">
                                            <SafeIcon icon={FiXCircle} className="text-red-500" />
                                            <span className="ml-1 text-red-500">Absent</span>
                                          </div>
                                        )}
                                      </td>
                                      <td className="p-2 text-center">
                                        {meeting.checkInTime ? format(new Date(meeting.checkInTime), 'h:mm a') : "-"}
                                      </td>
                                      <td className="p-2 text-right">
                                        <div className="flex items-center justify-end space-x-2">
                                          <button
                                            onClick={() => handleEditMeeting(meeting, member.id)}
                                            className="p-1.5 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
                                            title="Edit attendance record"
                                          >
                                            <SafeIcon icon={FiEdit2} className="text-blue-700" />
                                          </button>
                                          <button
                                            onClick={() => confirmDeleteAttendance(meeting, member.id)}
                                            className="p-1.5 bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                                            title="Delete attendance record"
                                          >
                                            <SafeIcon icon={FiTrash2} className="text-red-700" />
                                          </button>
                                        </div>
                                      </td>
                                    </>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="text-center py-4 text-gray-500">No meeting history available</p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              ))}
              {attendanceStats.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <SafeIcon icon={FiTrendingUp} className="mx-auto text-4xl mb-4" />
                  <p>No attendance data yet</p>
                  <p className="text-sm">Start taking attendance to see statistics</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default QuarterlyResults;